import { useCallback } from 'react';
import * as Cesium from 'cesium';
import { eventBus } from '../events';
import { computeCoordHash } from './useStreamHighlight';
import { userFolderService } from '../services/userFolderService';
export function useArtifactPromotion(params) {
    const { tools, userFolders, projectId, addStatus, onDeactivateTool, editingState, onEditComplete } = params;
    const { placemarksTool, measureTool, pathsTool, polygonTool, contourHighlight, streamHighlight, labelsTool } = tools;
    const handleAddItemToFolder = useCallback(async (itemId, itemType, folderId, snapshot) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        try {
            let name = '';
            let color = '';
            let displayUnit;
            let geometryJson = '';
            let icon;
            let showLabel;
            // Label-specific: when the user is saving edits to an already-
            // persisted label (hydrated from useServerArtifactRenderer.savedLabels),
            // we route through updateArtifact instead of createArtifact. The
            // hydrated copy carries the original artifact id here.
            let labelServerArtifactId;
            let labelServerFolderId;
            if (itemType === 'placemark') {
                const placemark = placemarksTool.placemarks.get(itemId);
                if (!placemark) {
                    console.error('[MapViewer] Placemark not found:', itemId);
                    return;
                }
                const cart = Cesium.Cartographic.fromCartesian(placemark.position);
                name = placemark.name;
                color = placemark.color;
                icon = placemark.icon;
                showLabel = placemark.showLabel;
                // Embed scale inside geometry_json (parity with how paths/polygons
                // embed lineWidth there). Renderer reads via `?? 1.0` so omitting
                // is also fine; we write explicitly for self-documenting rows.
                geometryJson = JSON.stringify({
                    lat: Cesium.Math.toDegrees(cart.latitude),
                    lng: Cesium.Math.toDegrees(cart.longitude),
                    scale: (_a = placemark.scale) !== null && _a !== void 0 ? _a : 1.0,
                });
            }
            else if (itemType === 'label') {
                const label = labelsTool.labels.get(itemId);
                if (!label) {
                    console.error('[MapViewer] Label not found:', itemId);
                    return;
                }
                const cart = Cesium.Cartographic.fromCartesian(label.position);
                name = label.name || label.text || 'Label';
                color = label.color;
                geometryJson = JSON.stringify({
                    lat: Cesium.Math.toDegrees(cart.latitude),
                    lng: Cesium.Math.toDegrees(cart.longitude),
                    // Persist terrain elevation so the DOM overlay re-projects to
                    // the right screen point on reload. Without this, fromDegrees
                    // anchors the label at the WGS84 ellipsoid (height 0) and the
                    // text drifts below the visible terrain in screen space.
                    height: cart.height,
                    text: label.text,
                    size: label.size,
                    stroke: label.stroke,
                    bold: label.bold,
                    italic: label.italic,
                    background: label.background,
                    cornerRadius: label.cornerRadius,
                    backgroundBorder: label.backgroundBorder,
                    fontFamily: label.fontFamily,
                    referenceDistance: label.referenceDistance
                });
                labelServerArtifactId = label.serverArtifactId;
                labelServerFolderId = label.serverFolderId;
            }
            else if (itemType === 'measurement') {
                const circle = (_b = measureTool.circles) === null || _b === void 0 ? void 0 : _b.get(itemId);
                if (circle) {
                    const centerCart = Cesium.Cartographic.fromCartesian(circle.center);
                    name = circle.name;
                    color = circle.color;
                    displayUnit = circle.displayUnit;
                    geometryJson = JSON.stringify({
                        centerLat: Cesium.Math.toDegrees(centerCart.latitude),
                        centerLng: Cesium.Math.toDegrees(centerCart.longitude),
                        radiusMeters: circle.radiusMeters,
                        areaUnit: circle.areaUnit,
                        lineWidth: (_c = circle.lineWidth) !== null && _c !== void 0 ? _c : 3,
                        outlineColor: circle.outlineColor,
                        fillOpacity: circle.fillOpacity,
                        showLabel: circle.showLabel,
                    });
                }
                else {
                    const measurement = measureTool.measurements.get(itemId);
                    if (!measurement) {
                        console.error('[MapViewer] Measurement not found:', itemId);
                        return;
                    }
                    const startCart = Cesium.Cartographic.fromCartesian(measurement.startPoint);
                    const endCart = Cesium.Cartographic.fromCartesian(measurement.endPoint);
                    name = measurement.name;
                    color = measurement.color;
                    displayUnit = measurement.displayUnit;
                    geometryJson = JSON.stringify({
                        startLat: Cesium.Math.toDegrees(startCart.latitude),
                        startLng: Cesium.Math.toDegrees(startCart.longitude),
                        endLat: Cesium.Math.toDegrees(endCart.latitude),
                        endLng: Cesium.Math.toDegrees(endCart.longitude),
                        lineWidth: (_d = measurement.lineWidth) !== null && _d !== void 0 ? _d : 5,
                        showLabel: measurement.showLabel,
                    });
                }
            }
            else if (itemType === 'path') {
                const path = pathsTool.paths.get(itemId);
                if (!path) {
                    console.error('[MapViewer] Path not found:', itemId);
                    return;
                }
                name = path.name;
                color = path.color;
                displayUnit = path.displayUnit;
                const points = path.points.map(p => {
                    const cart = Cesium.Cartographic.fromCartesian(p.position);
                    return {
                        lat: Cesium.Math.toDegrees(cart.latitude),
                        lng: Cesium.Math.toDegrees(cart.longitude)
                    };
                });
                // Persist the buffer alongside the line so a Save → reload roundtrip
                // doesn't drop the band. Stream highlights take the same approach
                // (see the stream_highlight branch below): config + polygons +
                // legacy single-ring fallback for older readers.
                const hasBuffer = typeof path.bufferDistanceMeters === 'number' &&
                    path.bufferDistanceMeters > 0 &&
                    Array.isArray(path.bufferPolygons) &&
                    path.bufferPolygons.length > 0;
                const bufferPayload = hasBuffer ? {
                    buffer: {
                        distance_meters: path.bufferDistanceMeters,
                        unit: (_e = path.bufferUnit) !== null && _e !== void 0 ? _e : 'ft',
                        area_sq_meters: path.bufferAreaSqMeters,
                        polygons: path.bufferPolygons,
                    }
                } : undefined;
                geometryJson = JSON.stringify({
                    points,
                    lineWidth: (_f = path.lineWidth) !== null && _f !== void 0 ? _f : 5,
                    ...(bufferPayload !== null && bufferPayload !== void 0 ? bufferPayload : {}),
                });
            }
            else if (itemType === 'polygon') {
                const polygon = polygonTool.polygons.get(itemId);
                if (!polygon) {
                    console.error('[MapViewer] Polygon not found:', itemId);
                    return;
                }
                name = polygon.name;
                color = polygon.color;
                displayUnit = polygon.displayUnit;
                const vertices = polygon.vertices.map(v => {
                    const cart = Cesium.Cartographic.fromCartesian(v.position);
                    return {
                        lat: Cesium.Math.toDegrees(cart.latitude),
                        lng: Cesium.Math.toDegrees(cart.longitude)
                    };
                });
                geometryJson = JSON.stringify({ vertices, lineWidth: (_g = polygon.lineWidth) !== null && _g !== void 0 ? _g : 5, fillOpacity: (_h = polygon.fillOpacity) !== null && _h !== void 0 ? _h : 0.3, outlineColor: polygon.outlineColor });
            }
            else if (itemType === 'contour_highlight') {
                // Snapshot is the caller's just-committed HighlightedContour;
                // use it directly to sidestep stale React state during the
                // commit-then-promote flow from the highlight dialog.
                const snapshotContour = snapshot;
                const highlight = snapshotContour !== null && snapshotContour !== void 0 ? snapshotContour : contourHighlight.highlightedContours.get(itemId);
                if (!highlight) {
                    console.error('[MapViewer] Contour highlight not found:', itemId);
                    return;
                }
                geometryJson = JSON.stringify({
                    source_artifact_id: highlight.artifactId,
                    entity_id: itemId,
                    contour_interval: highlight.contourInterval,
                    elevation: highlight.contourElevation || '',
                    coordinates: highlight.coordinates,
                    start_fraction: (_j = highlight.startFraction) !== null && _j !== void 0 ? _j : 0,
                    end_fraction: (_k = highlight.endFraction) !== null && _k !== void 0 ? _k : 1,
                    line_width: (_l = highlight.userLineWidth) !== null && _l !== void 0 ? _l : 3
                });
                name = highlight.name || `Contour at ${highlight.contourElevation || 'unknown'}`;
                color = highlight.highlightColor.toCssColorString();
            }
            else if (itemType === 'stream_highlight') {
                // Snapshot is the caller's just-committed Map<entityId, HighlightedStream>.
                // Use it to bypass state reads when promoting immediately after commit.
                const snapshotMap = snapshot;
                if (itemId.startsWith('stream-group-')) {
                    const firstEntityId = itemId.replace('stream-group-', '');
                    const groupEntityIds = snapshotMap
                        ? new Set(snapshotMap.keys())
                        : streamHighlight.findContiguousHighlighted(firstEntityId);
                    if (groupEntityIds.size === 0) {
                        console.error('[MapViewer] Stream highlight group not found:', itemId);
                        return;
                    }
                    const segments = [];
                    let firstHighlight = undefined;
                    groupEntityIds.forEach(entityId => {
                        var _a, _b, _c, _d;
                        const highlight = snapshotMap
                            ? snapshotMap.get(entityId)
                            : streamHighlight.highlightedStreams.get(entityId);
                        if (highlight) {
                            if (firstHighlight === undefined)
                                firstHighlight = highlight;
                            const legacyExterior = (_c = (_b = (_a = highlight.bufferPolygons) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.exterior) !== null && _c !== void 0 ? _c : [];
                            segments.push({
                                entityId,
                                coordHash: computeCoordHash(highlight.entity),
                                lineCoordinates: highlight.lineCoordinates,
                                bufferPolygons: (_d = highlight.bufferPolygons) !== null && _d !== void 0 ? _d : [],
                                bufferPolygon: legacyExterior,
                            });
                        }
                    });
                    if (!firstHighlight) {
                        console.error('[MapViewer] No highlights found in group:', itemId);
                        return;
                    }
                    const groupHighlight = firstHighlight;
                    geometryJson = JSON.stringify({
                        source_artifact_id: groupHighlight.artifactId,
                        entity_id: itemId,
                        stream_threshold: groupHighlight.streamThreshold,
                        buffer_settings: groupHighlight.bufferDistanceMeters > 0 ? {
                            distance_meters: groupHighlight.bufferDistanceMeters,
                            unit: groupHighlight.bufferUnit,
                            display_value: groupHighlight.bufferUnit === 'ft'
                                ? Math.round(groupHighlight.bufferDistanceMeters / 0.3048).toString()
                                : groupHighlight.bufferDistanceMeters.toString()
                        } : null,
                        segments: segments,
                        is_multi_segment: true
                    });
                    const segmentCount = groupEntityIds.size;
                    const defaultName = segmentCount > 1
                        ? `${groupHighlight.streamThreshold} Stream (${segmentCount} segments)`
                        : `${groupHighlight.streamThreshold} Stream`;
                    name = groupHighlight.name || defaultName;
                    color = groupHighlight.highlightColor.toCssColorString();
                }
                else {
                    const highlight = snapshotMap
                        ? snapshotMap.get(itemId)
                        : streamHighlight.highlightedStreams.get(itemId);
                    if (!highlight) {
                        console.error('[MapViewer] Stream highlight not found:', itemId);
                        return;
                    }
                    geometryJson = JSON.stringify({
                        source_artifact_id: highlight.artifactId,
                        entity_id: itemId,
                        coord_hash: computeCoordHash(highlight.entity),
                        stream_threshold: highlight.streamThreshold,
                        buffer_settings: highlight.bufferDistanceMeters > 0 ? {
                            distance_meters: highlight.bufferDistanceMeters,
                            unit: highlight.bufferUnit,
                            display_value: highlight.bufferUnit === 'ft'
                                ? Math.round(highlight.bufferDistanceMeters / 0.3048).toString()
                                : highlight.bufferDistanceMeters.toString()
                        } : null,
                        line_coordinates: highlight.lineCoordinates,
                        buffer_polygons: (_m = highlight.bufferPolygons) !== null && _m !== void 0 ? _m : [],
                        // Legacy field — first polygon's exterior. Kept populated so older
                        // clients reading this artifact still see a (hole-less) polygon.
                        buffer_polygon: (_q = (_p = (_o = highlight.bufferPolygons) === null || _o === void 0 ? void 0 : _o[0]) === null || _p === void 0 ? void 0 : _p.exterior) !== null && _q !== void 0 ? _q : [],
                    });
                    name = highlight.name || `${highlight.streamThreshold} Stream`;
                    color = highlight.highlightColor.toCssColorString();
                }
            }
            // If editing a server artifact, update it instead of creating new
            if (editingState && editingState.serverArtifactId) {
                await userFolderService.updateArtifact({
                    artifactId: editingState.serverArtifactId,
                    name,
                    color,
                    displayUnit,
                    geometryJson,
                    icon,
                    showLabel
                });
                // Refresh folders FIRST so the renderer can detect the geometry change and replace
                // the stale entities. Then run onEditComplete (which un-hides the artifact via
                // cancelEdit) so visibility flips on the new entities, not the old ones.
                await userFolders.fetchFolders();
                onEditComplete === null || onEditComplete === void 0 ? void 0 : onEditComplete();
            }
            else if (itemType === 'label' && labelServerArtifactId) {
                // Saving edits to a hydrated saved label — drive updateArtifact
                // directly; the hydrated local copy is removed below (the
                // existing label-cleanup branch).
                await userFolderService.updateArtifact({
                    artifactId: labelServerArtifactId,
                    name,
                    color,
                    displayUnit,
                    geometryJson,
                    // Allow folder reassignment if the user moved it via the
                    // pop-out, but default to its original folder so a plain
                    // "Save changes" doesn't accidentally move it.
                    folderId: folderId || labelServerFolderId
                });
                await userFolders.fetchFolders();
            }
            else {
                const effectiveArtifactType = itemType === 'measurement' && ((_r = measureTool.circles) === null || _r === void 0 ? void 0 : _r.has(itemId))
                    ? 'measurement_circle'
                    : itemType;
                const savedArtifact = await userFolders.createArtifact({
                    folderId,
                    artifactType: effectiveArtifactType,
                    name,
                    color,
                    displayUnit,
                    geometryJson,
                    icon,
                    showLabel
                });
                if (projectId && savedArtifact) {
                    // Extract an anchor coord from the artifact's geometry for point-shaped
                    // artifacts so any associated draft comments can be promoted as
                    // coord-anchored comments. Multi-point geometries (path/polygon) omit
                    // the anchor; their drafts get skipped at promotion time.
                    let anchor;
                    try {
                        const g = JSON.parse(geometryJson);
                        if (itemType === 'placemark' || itemType === 'label') {
                            if (typeof g.lat === 'number' && typeof g.lng === 'number') {
                                anchor = { latitude: g.lat, longitude: g.lng };
                                if (typeof g.height === 'number')
                                    anchor.height = g.height;
                            }
                        }
                        else if (itemType === 'measurement') {
                            if (typeof g.startLat === 'number' && typeof g.startLng === 'number') {
                                anchor = { latitude: g.startLat, longitude: g.startLng };
                            }
                        }
                    }
                    catch (_u) {
                        // Geometry was malformed; promote without an anchor so any drafts get skipped explicitly.
                    }
                    eventBus.emit('userArtifact:promoted', {
                        projectId,
                        unsavedItemId: itemId,
                        savedArtifactId: savedArtifact.id,
                        savedArtifactName: savedArtifact.name,
                        savedArtifactType: savedArtifact.artifactType,
                        savedArtifactColor: savedArtifact.color,
                        anchor,
                    });
                }
            }
            if (itemType === 'placemark') {
                placemarksTool.removePlacemark(itemId);
            }
            else if (itemType === 'label') {
                labelsTool.removeLabel(itemId);
            }
            else if (itemType === 'measurement') {
                if ((_s = measureTool.circles) === null || _s === void 0 ? void 0 : _s.has(itemId)) {
                    (_t = measureTool.removeCircle) === null || _t === void 0 ? void 0 : _t.call(measureTool, itemId);
                }
                else {
                    measureTool.removeMeasurement(itemId);
                }
            }
            else if (itemType === 'path') {
                pathsTool.removePath(itemId);
            }
            else if (itemType === 'polygon') {
                polygonTool.removePolygon(itemId);
            }
            else if (itemType === 'contour_highlight') {
                contourHighlight.unhighlightContour(itemId);
            }
            else if (itemType === 'stream_highlight') {
                // Pass each just-committed HighlightedStream into unhighlightStream
                // so cleanup doesn't depend on React state having flushed since
                // confirmHighlight ran in the same tick.
                const snapshotMap = snapshot;
                if (snapshotMap && snapshotMap.size > 0) {
                    snapshotMap.forEach((highlight, entityId) => streamHighlight.unhighlightStream(entityId, highlight));
                }
                else if (itemId.startsWith('stream-group-')) {
                    const firstEntityId = itemId.replace('stream-group-', '');
                    const groupEntityIds = streamHighlight.findContiguousHighlighted(firstEntityId);
                    groupEntityIds.forEach(entityId => streamHighlight.unhighlightStream(entityId));
                }
                else {
                    streamHighlight.unhighlightStream(itemId);
                }
            }
            onDeactivateTool === null || onDeactivateTool === void 0 ? void 0 : onDeactivateTool();
        }
        catch (err) {
            // The local copy is deliberately left in place (the cleanup below the
            // write never ran), so the drawing survives for a retry. Rethrow so the
            // save form that called this shows the server's reason inline.
            console.error('[MapViewer] Failed to add item to folder:', err);
            throw err;
        }
    }, [userFolders, placemarksTool, measureTool, pathsTool, polygonTool, labelsTool, contourHighlight, streamHighlight, projectId, onDeactivateTool, editingState, onEditComplete]);
    const handlePolygonFileUpload = useCallback(async (files) => {
        if (!files || files.length === 0)
            return;
        const { parseGeoFile, extractPolygons, sanitizeCoordinates } = await import('../utils/geoFileParser');
        const { geojson, error } = await parseGeoFile(files[0]);
        if (error || !geojson) {
            addStatus(error || 'Failed to parse file');
            return;
        }
        const polygons = extractPolygons(geojson);
        if (polygons.length === 0) {
            addStatus('No polygons found in file');
            return;
        }
        let importedCount = 0;
        polygons.forEach(p => {
            const coords = sanitizeCoordinates(p.coordinates);
            if (coords.length >= 3) {
                polygonTool.createPolygonFromCoordinates(coords, p.name);
                importedCount++;
            }
        });
        if (importedCount > 0) {
            addStatus(`Imported ${importedCount} polygon(s)`);
        }
        else {
            addStatus('No valid polygons imported');
        }
    }, [polygonTool, addStatus]);
    const handlePlacemarkFileUpload = useCallback(async (files) => {
        if (!files || files.length === 0)
            return;
        const { parseGeoFile, extractPoints, sanitizeCoordinates } = await import('../utils/geoFileParser');
        const { geojson, error } = await parseGeoFile(files[0]);
        if (error || !geojson) {
            addStatus(error || 'Failed to parse file');
            return;
        }
        const points = extractPoints(geojson);
        if (points.length === 0) {
            addStatus('No placemarks found in file');
            return;
        }
        let importedCount = 0;
        points.forEach(p => {
            const coords = sanitizeCoordinates(p.coordinates);
            if (coords.length >= 1) {
                placemarksTool.addPlacemark(coords[0].lat, coords[0].lng, p.name);
                importedCount++;
            }
        });
        if (importedCount > 0) {
            addStatus(`Imported ${importedCount} placemark(s)`);
        }
        else {
            addStatus('No valid placemarks imported');
        }
    }, [placemarksTool, addStatus]);
    const handlePathFileUpload = useCallback(async (files) => {
        if (!files || files.length === 0)
            return;
        const { parseGeoFile, extractLineStrings, sanitizeCoordinates } = await import('../utils/geoFileParser');
        const { geojson, error } = await parseGeoFile(files[0]);
        if (error || !geojson) {
            addStatus(error || 'Failed to parse file');
            return;
        }
        const lineStrings = extractLineStrings(geojson);
        if (lineStrings.length === 0) {
            addStatus('No paths (LineString) found in file');
            return;
        }
        let importedCount = 0;
        lineStrings.forEach(ls => {
            const coords = sanitizeCoordinates(ls.coordinates);
            if (coords.length >= 2) {
                pathsTool.createPathFromCoordinates(coords, ls.name);
                importedCount++;
            }
        });
        if (importedCount > 0) {
            addStatus(`Imported ${importedCount} path(s)`);
        }
    }, [pathsTool, addStatus]);
    return {
        handleAddItemToFolder,
        handlePathFileUpload,
        handlePolygonFileUpload,
        handlePlacemarkFileUpload,
    };
}
